import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ShieldCheck, ShieldOff, ShieldPlus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Badge, Banner, Button, Card, Dialog, Field, Input, useUi } from '../../components/ui';
import { SecretBox } from '../users/shared';

interface SetupData { secret: string; otpauthUrl: string; qrDataUrl: string }

/** "JBSWY3DPEHPK3PXP" → "JBSW Y3DP EHPK 3PXP" */
export const groupSecret = (s: string) => (s.match(/.{1,4}/g) ?? []).join(' ');

export function TwoFactorCard({ required, startSignal }: { required: boolean; startSignal: number }) {
  const { me, refresh } = useAuth();
  const { toast, toastError } = useUi();
  const enabled = !!me?.user.totpEnabled;
  const isAdmin = me?.user.role === 'admin';

  const [setup, setSetup] = useState<SetupData | null>(null);
  const [starting, setStarting] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  async function start() {
    if (starting || enabled) return;
    setStarting(true);
    try {
      setSetup(await api.post<SetupData>('/auth/2fa/setup'));
    } catch (e) {
      toastError(e);
    } finally {
      setStarting(false);
    }
  }

  // Start von außen (Hinweis-Banner oben auf der Seite)
  const lastSignal = useRef(startSignal);
  useEffect(() => {
    if (startSignal !== lastSignal.current) {
      lastSignal.current = startSignal;
      void start();
    }
  });

  return (
    <Card title={<h2 id="zwei-faktor">Zwei-Faktor-Anmeldung</h2>} actions={enabled ? <Badge tone="good"><ShieldCheck size={12} /> Aktiv</Badge> : <Badge tone={isAdmin ? 'warn' : undefined}>Aus</Badge>}>
      <div className="stack">
        {enabled ? (
          <>
            <p className="muted" style={{ margin: 0 }}>
              Bei der Anmeldung brauchst du zusätzlich zum Passwort einen 6-stelligen Code aus deiner Authenticator-App. So bleibt dein Konto sicher, auch wenn jemand dein Passwort kennt.
            </p>
            {isAdmin && required ? (
              <div className="small subtle">Für Administratoren vorgeschrieben – kann nicht ausgeschaltet werden.</div>
            ) : (
              <div><Button variant="danger" icon={<ShieldOff size={16} />} onClick={() => setDisableOpen(true)}>Ausschalten</Button></div>
            )}
          </>
        ) : (
          <>
            <p className="muted" style={{ margin: 0 }}>
              Schützt dein Konto zusätzlich mit einem Code vom Handy. Wer nur dein Passwort kennt, kommt dann nicht hinein.
              {isAdmin && ' Für Administratoren – besonders mit Fernzugriff übers Internet – dringend empfohlen.'}
            </p>
            <div>
              <Button variant="primary" icon={<ShieldPlus size={16} />} loading={starting} onClick={() => void start()}>Jetzt einrichten</Button>
            </div>
          </>
        )}
      </div>

      {setup && (
        <SetupDialog
          data={setup}
          onClose={() => setSetup(null)}
          onDone={async () => {
            setSetup(null);
            await refresh();
            toast('Zwei-Faktor-Anmeldung ist jetzt aktiv', 'success');
          }}
        />
      )}
      {disableOpen && (
        <DisableDialog
          onClose={() => setDisableOpen(false)}
          onDone={async () => {
            setDisableOpen(false);
            await refresh();
            toast('Zwei-Faktor-Anmeldung ausgeschaltet', 'success');
          }}
        />
      )}
    </Card>
  );
}

function SetupDialog({ data, onClose, onDone }: { data: SetupData; onClose: () => void; onDone: () => Promise<void> }) {
  const [code, setCode] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!/^\d{6}$/.test(code)) { setError('Bitte den 6-stelligen Code aus der App eingeben.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/2fa/enable', { code });
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aktivieren fehlgeschlagen.');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Zwei-Faktor-Anmeldung einrichten"
      footer={<>
        <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
        <Button variant="primary" type="submit" form="tfa-form" loading={busy} disabled={code.length !== 6}>Aktivieren</Button>
      </>}
    >
      <form id="tfa-form" className="adm-dialog" onSubmit={submit}>
        <ol className="adm-steps">
          <li>
            <strong>App installieren.</strong>{' '}
            <span className="muted">Lade auf deinem Handy eine Authenticator-App herunter, z. B. <em>Google Authenticator</em> oder <em>Microsoft Authenticator</em> (kostenlos im App Store bzw. Google Play).</span>
          </li>
          <li>
            <div><strong>QR-Code scannen.</strong> <span className="muted">Tippe in der App auf „+“ bzw. „Konto hinzufügen“ und scanne diesen Code:</span></div>
            <div className="adm-qr" style={{ marginTop: 10 }}>
              <img src={data.qrDataUrl} alt="QR-Code für die Authenticator-App" width={200} height={200} />
            </div>
            <div className="small muted" style={{ margin: '10px 0 6px' }}>Scannen klappt nicht? Gib stattdessen diesen Schlüssel von Hand ein (Typ „zeitbasiert“):</div>
            <SecretBox value={data.secret} display={groupSecret(data.secret)} small />
          </li>
          <li>
            <div style={{ marginBottom: 8 }}><strong>Code eingeben.</strong> <span className="muted">Die App zeigt jetzt einen 6-stelligen Code, der sich alle 30 Sekunden ändert.</span></div>
            {error && <Banner tone="crit">{error}</Banner>}
            <Field htmlFor="tfa-code" label="Code aus der App">
              <Input
                id="tfa-code"
                ref={codeRef}
                className="adm-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </Field>
          </li>
        </ol>
      </form>
    </Dialog>
  );
}

function DisableDialog({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || !password) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/2fa/disable', { password });
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ausschalten fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Zwei-Faktor-Anmeldung ausschalten?"
      footer={<>
        <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
        <Button variant="danger-solid" type="submit" form="tfa-off-form" loading={busy} disabled={!password}>Ausschalten</Button>
      </>}
    >
      <form id="tfa-off-form" className="stack adm-dialog" onSubmit={submit}>
        <p style={{ margin: 0 }}>Dein Konto ist danach nur noch mit dem Passwort geschützt. Bitte bestätige mit deinem Passwort.</p>
        {error && <Banner tone="crit">{error}</Banner>}
        <Field label="Passwort" htmlFor="tfa-pw">
          <Input id="tfa-pw" type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </form>
    </Dialog>
  );
}
